import { useState } from 'react';
import DashboardHeader from '../components/DashboardHeader';
import Sidebar from '../components/Sidebar';
import DiscussionCard from './components/DiscussionCard';
import AddDiscussionModal from './components/AddDiscussionModal';
import EditDiscussionModal from './components/EditDiscussionModal';

export interface Discussion {
  id: number;
  title: string;
  content: string;
  category: string;
  tags: string[];
  color: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function Discussions() {
  const [discussions, setDiscussions] = useState<Discussion[]>([
    {
      id: 1,
      title: 'Q4 Marketing Strategy',
      content: 'Focus on digital marketing campaigns targeting health-conscious millennials. Key channels: Instagram, YouTube wellness influencers, and organic search optimization. Budget allocation: 40% social media, 30% content marketing, 30% partnerships.',
      category: 'Strategy',
      tags: ['marketing', 'Q4', 'digital'],
      color: 'emerald',
      isPinned: true,
      createdAt: '2024-01-15',
      updatedAt: '2024-01-20'
    },
    {
      id: 2,
      title: 'Product Development Ideas',
      content: 'New product line ideas: Moringa energy bars, ready-to-drink moringa smoothies, moringa-infused cooking oil. Research needed on market demand and production costs. Target launch: Q2 2024.',
      category: 'Product',
      tags: ['innovation', 'R&D', 'new products'],
      color: 'blue',
      isPinned: true,
      createdAt: '2024-01-18',
      updatedAt: '2024-01-22'
    },
    {
      id: 3,
      title: 'Team Meeting Notes - Jan 20',
      content: 'Discussed expansion plans for Maharashtra region. Action items: 1) Research distribution partners in Mumbai and Nagpur, 2) Prepare cost analysis for new warehouse, 3) Schedule meetings with potential retail partners. Follow-up meeting scheduled for Feb 5.',
      category: 'Meeting',
      tags: ['expansion', 'team', 'action items'],
      color: 'purple',
      isPinned: false,
      createdAt: '2024-01-20',
      updatedAt: '2024-01-20'
    },
    {
      id: 4,
      title: 'Customer Feedback Summary',
      content: 'Positive feedback on product quality and packaging. Common requests: larger pack sizes, subscription options, more recipe ideas. Complaints: delivery delays in rural areas. Action: Improve logistics network and create recipe blog section.',
      category: 'Feedback',
      tags: ['customers', 'improvement', 'insights'],
      color: 'orange',
      isPinned: false,
      createdAt: '2024-01-12',
      updatedAt: '2024-01-19'
    },
    {
      id: 5,
      title: 'Partnership Opportunities',
      content: 'Potential partnerships: 1) Yoga studios for product placement, 2) Organic food stores for retail distribution, 3) Corporate wellness programs for bulk orders. Next steps: Prepare partnership proposals and pricing models.',
      category: 'Business',
      tags: ['partnerships', 'growth', 'B2B'],
      color: 'teal',
      isPinned: false,
      createdAt: '2024-01-10',
      updatedAt: '2024-01-15'
    },
    {
      id: 6,
      title: 'Sustainability Initiatives',
      content: 'Goals: 100% biodegradable packaging by Q3, carbon-neutral shipping by end of year, support local farmers with fair trade practices. Research eco-friendly packaging suppliers and calculate carbon offset costs.',
      category: 'Sustainability',
      tags: ['environment', 'CSR', 'packaging'],
      color: 'green',
      isPinned: false,
      createdAt: '2024-01-08',
      updatedAt: '2024-01-14'
    }
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingDiscussion, setEditingDiscussion] = useState<Discussion | null>(null);

  const categories = ['All', 'Strategy', 'Product', 'Meeting', 'Feedback', 'Business', 'Sustainability'];
  const colors = ['emerald', 'blue', 'purple', 'orange', 'teal', 'green', 'pink', 'yellow'];

  const filteredDiscussions = discussions.filter(discussion => {
    const matchesSearch = 
      discussion.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      discussion.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      discussion.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All' || discussion.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const pinnedDiscussions = filteredDiscussions.filter(d => d.isPinned);
  const unpinnedDiscussions = filteredDiscussions.filter(d => !d.isPinned);

  const handleAddDiscussion = (newDiscussion: Omit<Discussion, 'id' | 'createdAt' | 'updatedAt'>) => {
    const discussion: Discussion = {
      ...newDiscussion,
      id: Math.max(...discussions.map(d => d.id), 0) + 1,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0]
    };
    setDiscussions([discussion, ...discussions]);
    setIsAddModalOpen(false);
  };

  const handleEditDiscussion = (updatedDiscussion: Discussion) => {
    setDiscussions(discussions.map(d => 
      d.id === updatedDiscussion.id 
        ? { ...updatedDiscussion, updatedAt: new Date().toISOString().split('T')[0] }
        : d
    ));
    setEditingDiscussion(null);
  };

  const handleDeleteDiscussion = (id: number) => {
    if (confirm('Are you sure you want to delete this discussion?')) {
      setDiscussions(discussions.filter(d => d.id !== id));
    }
  };

  const handleTogglePin = (id: number) => {
    setDiscussions(discussions.map(d => 
      d.id === id ? { ...d, isPinned: !d.isPinned } : d
    ));
  };

  const handleDuplicateDiscussion = (discussion: Discussion) => {
    const newDiscussion: Discussion = {
      ...discussion,
      id: Math.max(...discussions.map(d => d.id), 0) + 1,
      title: `${discussion.title} (Copy)`,
      isPinned: false,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0]
    };
    setDiscussions([newDiscussion, ...discussions]);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashboardHeader />
        
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Discussions</h1>
                  <p className="text-gray-600 mt-1">Document your discussions, strategies, and ideas</p>
                </div>
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line w-5 h-5 flex items-center justify-center"></i>
                  New Discussion
                </button>
              </div>

              {/* Search and Filters */}
              <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <i className="ri-search-line w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search discussions, tags, or content..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  )}
                </div>

                {/* Category Filters and View Toggle */}
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {categories.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          selectedCategory === category
                            ? 'bg-emerald-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                        viewMode === 'grid'
                          ? 'bg-white text-emerald-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-grid-line w-5 h-5 flex items-center justify-center"></i>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer ${
                        viewMode === 'list'
                          ? 'bg-white text-emerald-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <i className="ri-list-check w-5 h-5 flex items-center justify-center"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Count */}
            {searchQuery && (
              <div className="mb-4 text-sm text-gray-600">
                Found {filteredDiscussions.length} discussion{filteredDiscussions.length !== 1 ? 's' : ''}
              </div>
            )}

            {/* Pinned Discussions */}
            {pinnedDiscussions.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                  <i className="ri-pushpin-fill w-4 h-4 flex items-center justify-center"></i>
                  Pinned
                </h2>
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                  : 'space-y-3'
                }>
                  {pinnedDiscussions.map(discussion => (
                    <DiscussionCard
                      key={discussion.id}
                      discussion={discussion}
                      viewMode={viewMode}
                      onEdit={() => setEditingDiscussion(discussion)}
                      onDelete={() => handleDeleteDiscussion(discussion.id)}
                      onTogglePin={() => handleTogglePin(discussion.id)}
                      onDuplicate={() => handleDuplicateDiscussion(discussion)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Other Discussions */}
            {unpinnedDiscussions.length > 0 && (
              <div>
                {pinnedDiscussions.length > 0 && (
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                    Others
                  </h2>
                )}
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                  : 'space-y-3'
                }>
                  {unpinnedDiscussions.map(discussion => (
                    <DiscussionCard
                      key={discussion.id}
                      discussion={discussion}
                      viewMode={viewMode}
                      onEdit={() => setEditingDiscussion(discussion)}
                      onDelete={() => handleDeleteDiscussion(discussion.id)}
                      onTogglePin={() => handleTogglePin(discussion.id)}
                      onDuplicate={() => handleDuplicateDiscussion(discussion)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {filteredDiscussions.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-discuss-line w-16 h-16 flex items-center justify-center text-6xl text-gray-300 mx-auto mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {searchQuery ? 'No discussions found' : 'No discussions yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery 
                    ? 'Try adjusting your search or filters'
                    : 'Start documenting your discussions and strategies'
                  }
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line w-5 h-5 flex items-center justify-center"></i>
                    Create First Discussion
                  </button>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {isAddModalOpen && (
        <AddDiscussionModal
          colors={colors}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddDiscussion}
        />
      )}

      {editingDiscussion && (
        <EditDiscussionModal
          discussion={editingDiscussion}
          colors={colors}
          onClose={() => setEditingDiscussion(null)}
          onSave={handleEditDiscussion}
        />
      )}
    </div>
  );
}
